import React, { useContext } from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Device, Call as TwilioCall } from '@twilio/voice-sdk';
import moment, { Moment } from 'moment';

// api
import client from 'sources/api';

// components
import Button from 'components/Button';
import CreateNoteTagModal from 'components/CreateNoteTagModal';
import TemplatedMessageButton from 'components/TemplatedMessageButton';
import { Grid, IconButton, Typography, Box, Snackbar } from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';

// icons
import {
  Call,
  CallEnd,
  SettingsPhone,
  PhoneInTalk,
  Mic,
  MicOff,
  NoteAdd,
} from '@material-ui/icons';

// selectors
import getCurrentGroupId from 'store/selectors/getCurrentGroupId';
import getCurrentUser from 'store/selectors/getCurrentUser';
import getSliceState from 'store/selectors/getSliceState';

// actions
import { fetchPhoneNumbersForGroup as fetchPhoneNumbersForGroupAction } from 'store/actions/phoneNumberActions';

// helpers
import { SiteVisualDataContext } from 'components/SiteVisualData';

type Props = {
  id: number;
  phoneNumber: string;
  leadsLoading: boolean;
  lastCall: boolean;
  onNextCall: () => void;
  onCallEnd: () => void;
};

export default function CallPanel({
  id,
  phoneNumber,
  leadsLoading,
  lastCall,
  onNextCall,
  onCallEnd,
}: Props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { callPanelMessage } = useContext(SiteVisualDataContext);
  const currentGroupId = useSelector(getCurrentGroupId);
  const currentUser = useSelector(getCurrentUser);
  const phoneNumbers = useSelector(getSliceState('phoneNumber', null, true));
  const currentUserId = currentUser.get('id');

  const [duration, setDuration] = useState<Moment | null>(null);
  const [callSid, setCallSid] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [noteOpened, setNoteOpened] = useState(false);
  const [device, setDevice] = useState<Device | null>(null);
  const [activeCall, setActiveCall] = useState<TwilioCall | null>(null);
  const isCallEnded = !activeCall && Boolean(duration);

  useEffect(() => {
    dispatch(fetchPhoneNumbersForGroupAction());
  }, [dispatch]);

  useEffect(() => {
    if (!(phoneNumbers as any).loading) {
      client
        .get(`/calls/token?group_id=${currentGroupId}`)
        .then((response: any) => {
          const newDevice = new Device(response.data.token);
          setDevice(newDevice);

          // Register the device to receive incoming calls
          newDevice.register();
          newDevice.on('registered', () => {
            setLoading(false);
          });

          newDevice.on('error', () => {
            setError(true);
          });
        })
        .catch(() => {
          setError(true);
        });
    }
  }, [currentGroupId, phoneNumbers]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (device) {
        device.destroy();
      }
    };
  }, [device]);

  useEffect(() => {
    if (activeCall) {
      activeCall.on('accept', () => {
        setDuration(moment().startOf('day'));
      });
      activeCall.on('disconnect', () => {
        setCallSid(activeCall.parameters.CallSid);
        setActiveCall(null);
        onCallEnd();
      });
    }
  }, [activeCall, onNextCall, onCallEnd]);

  useEffect(() => {
    if (duration && activeCall) {
      const timer = setInterval(() => {
        setDuration(duration => moment(duration?.add(1, 'second')));
      }, 1000);
      return () => {
        clearInterval(timer);
      };
    }
  }, [duration, activeCall]);

  const currentUserHasPhoneNumber = (phoneNumbers as any).data.find(
    (phoneNumber: any) => phoneNumber.account === currentUserId
  );

  const startCall = async () => {
    if (device) {
      try {
        const outgoingCall = await device.connect({
          params: {
            type: 'outgoing',
            group_id: currentGroupId,
            account_id: currentUserId,
            pnm_id: id.toString(),
          },
        });
        setActiveCall(outgoingCall);
      } catch (error) {
        setError(true);
      }
    }
  };

  const endCall = () => {
    if (activeCall) {
      activeCall.disconnect();
    }
  };

  const toggleMuted = () => {
    if (activeCall) activeCall.mute(!activeCall.isMuted());
  };

  const handleClickNext = () => {
    setDuration(null);
    onNextCall();
  };

  const handleNoteClose = () => {
    setCallSid(null);
    setNoteOpened(false);
  };

  const handleNoteOpen = () => setNoteOpened(true);
  const handleClick = () => navigate('/settings/phone');

  return (phoneNumbers as any).loading || currentUserHasPhoneNumber ? (
    <>
      <Box padding={2} clone>
        <Grid
          container
          spacing={2}
          direction='column'
          alignItems='center'
          justifyContent='center'
        >
          <Grid item>
            <Box
              bgcolor='background.darkPaper'
              borderRadius='50%'
              display='inline-flex'
              padding={5}
            >
              <Box clone fontSize={60}>
                {activeCall ? (
                  duration ? (
                    <PhoneInTalk color='disabled' />
                  ) : (
                    <SettingsPhone color='disabled' />
                  )
                ) : isCallEnded ? (
                  <CallEnd color='disabled' />
                ) : (
                  <Call color='disabled' />
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item>
            <Typography>
              {leadsLoading ? (
                <Skeleton width={160} height={36} />
              ) : (
                phoneNumber
              )}
            </Typography>
          </Grid>
          <Grid item>
            {isCallEnded ? 'Call Ended' : duration && duration.format('mm:ss')}
          </Grid>
          <Grid item></Grid>
          <Grid item>
            {activeCall && (
              <Button
                id='note'
                style={{ marginTop: 18 }}
                fullWidth
                variant='outlined'
                startIcon={<NoteAdd />}
                onClick={handleNoteOpen}
              >
                Write Note
              </Button>
            )}
          </Grid>
          <Grid item>
            {activeCall ? (
              <Grid container>
                <Grid item>
                  <IconButton onClick={toggleMuted} id='muteToggle'>
                    {activeCall && activeCall.isMuted() ? <MicOff /> : <Mic />}
                  </IconButton>
                </Grid>
                <Grid item>
                  <Box
                    sx={{
                      // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
                      backgroundColor: 'rgb(255, 0, 0)',
                      '&:hover': {
                        backgroundColor: 'rgb(255, 0, 0, 0.7)',
                      },
                    }}
                    clone
                  >
                    <IconButton onClick={endCall} id='endCall'>
                      <CallEnd />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            ) : isCallEnded ? (
              <Grid container direction='column' spacing={2}>
                <Grid item>
                  <TemplatedMessageButton leadId={id} />
                </Grid>
                <Grid item>
                  <Button
                    id='callBack'
                    variant='outlined'
                    color='primary'
                    fullWidth
                    onClick={startCall}
                  >
                    Call Back
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    id='startCall'
                    variant='contained'
                    color='primary'
                    fullWidth
                    onClick={handleClickNext}
                  >
                    {lastCall ? 'Done' : 'Next'}
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Box minWidth={120}>
                <Button
                  id='startCall'
                  variant='contained'
                  color='primary'
                  fullWidth
                  onClick={startCall}
                  disabled={error}
                  loading={
                    loading || (phoneNumbers as any).loading || leadsLoading
                  }
                >
                  Call
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      <Snackbar open={error} autoHideDuration={6000}>
        <Alert severity='error'>Something went wrong...</Alert>
      </Snackbar>
      <CreateNoteTagModal
        leadIds={[id]}
        open={noteOpened || Boolean(callSid)}
        disabled={Boolean(activeCall)}
        callSid={callSid}
        onClose={handleNoteClose}
      />
    </>
  ) : (
    <Box padding={4}>
      <Grid
        container
        spacing={2}
        alignItems='center'
        justifyContent='center'
        direction='column'
      >
        <Grid item>
          <Typography variant='subtitle2'>{callPanelMessage}</Typography>
          <Typography color='textSecondary' variant='body2'>
            Create and assign a phone number to start making calls!
          </Typography>
        </Grid>
        <Grid item>
          <Button variant='outlined' onClick={handleClick}>
            Set Up Number
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
